import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, 
  Input, 
  Select, 
  Radio, 
  DatePicker, 
  TimePicker, 
  message, 
  Checkbox, 
  Button, 
  Card, 
  Row, 
  Col, 
  Space, 
  Typography, 
  Modal,
  Tag,
  Alert,
  Skeleton,
  List,
  Avatar,
  Table,
  InputNumber,
} from 'antd';
import { 
  UserOutlined, 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import SwitchShop from '../../shop/switch-shop';
import { getCurrentShopId } from '../../shop/get-current-shop';
import usePagination from '../../../components/pagination';

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const columns = [
  {
    title: '客户名称',
    dataIndex: 'username',
  },
  {
    title: '手机号码',
    dataIndex: 'phoneNo',
  },
  {
    title: '消费金额',
    dataIndex: 'totalConsume',
    render: (text) => <Text>{text/100}元</Text>
  },
  {
    title: '注册时间',
    dataIndex: 'createTime',
    render: (text) => <Text>{dayjs(text).format('YYYY-MM-DD')}</Text>
  },
  {
    title: '备注',
    dataIndex: 'remark',
  }
]

const MarketingTaskForm = () => {
  const [form] = Form.useForm();
  const [sendForm] = Form.useForm();
  const [taskStatus, setTaskStatus] = useState(0);
  const url = new URL(window.location.href);
  const [taskType, setTaskType] = useState('ONCE');
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [signs, setSigns] = useState([]);
  const [selectedSign, setSelectedSign] = useState({});
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedTotal, setSelectedTotal] = useState(0);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [periodicConfigVisible, setPeriodicConfigVisible] = useState(false);
  const { pagination, setTotal, setCurrent } = usePagination({
    onChange: (page, pageSize) => {
        handleFilterChange(page, pageSize);
    },
  });

  const fetchTaskDetail = async (taskId) =>  { 
      const shopId = await getCurrentShopId();
      const response = await fetch( `/api/promotion/sms/task/detail?shopId=${shopId}&taskId=${taskId}`);
      const result = await response.json();

      if(result && result.success) {
          return result.data;
      }else {
          message.error(result.message);
          return null;
      }
  };

  const initFome = async (taskId) => {
    
    const task = await fetchTaskDetail(taskId);

    const { customerCondition: con = {}} = task;
    
    const initData = {
      ...task,
      ...task.customerCondition,
      ...task.smsContent,
      sendTime: dayjs(task.sendTime),
      createTimeStart: con.createTimeStart ? dayjs(con.createTimeStart) : undefined,
      createTimeEnd: con.createTimeEnd ? dayjs(con.createTimeEnd) : undefined,
    }

    form.setFieldsValue(initData);

    setSelectedSign({signName: task?.smsContent?.signName})
    setSelectedTemplate({
      templateCode: task?.smsContent?.templateCode,
      templateContent: task?.smsContent?.templateContent,
    });
    setTaskStatus(task.status);

    fetchCustomers(task.customerCondition);
  }
  // 发送短信
  const sendSms = async (phoneNo, templateCode, signName, params) => { 
    const shopId = await getCurrentShopId();
    const response = await fetch('/api/sms/send/batch-condition', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneNo,
        templateCode,
        signName,
        params,
        shopId,
      }),
    });

    const result = await response.json();

    if(result.success && result?.data?.body?.code === 'OK') {
      message.success('发送成功');
    } else {
      Modal.error({title: '发送失败', content: <div><div>{result?.data?.body?.message}</div><div>请联系技术支持！</div></div>});
    }
  };

  // 测试发送
  const handleTestSend = async () => { 
    const templateCode = selectedTemplate.templateCode;
    const templateContent = selectedTemplate.templateContent;
    const signName = selectedSign.signName;

    if(!templateCode || !signName) {
      message.error('请选择短信模板和签名');
      return;
    }

    Modal.confirm({
      title: '测试发送',
      content: <Form form={sendForm} layout='vertical'>
        <Form.Item label='手机号码' name='phoneNo' 
          rules={[{required: true, message: '请输入手机号码'}]}
          help='该手机号须为该店铺客户的手机号'
        >
          <Input id='phoneNo' placeholder='请输入测试发送的手机号码' />
        </Form.Item>
      </Form>,
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        const phoneNo = sendForm.getFieldValue('phoneNo');
               
        // 发送短信
        sendSms(phoneNo, templateCode, signName);
      }
    })
  };
  // 获取短信模板
  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      // 实际应用中替换为API调用
      const response = await fetch('/api/promotion/sms/template/list');
      const result = await response.json();

      if(result.success && result?.data?.code === 'OK') {
        let data = result.data?.smsTemplateList || [];
        data = data.filter(t => t.auditStatus === 'AUDIT_STATE_PASS' && !(/\${.*?}/.test(t.templateContent)));
        setTemplates(data);
      }
      
      
    } catch (error) {
      console.error('获取模板失败:', error);
      Modal.error({
        title: '错误',
        content: '获取短信模板失败，请稍后重试',
      });
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // 获取短信签名列表
  const fetchSigns = useCallback(async () => {

    try {
      // 获取签名列表
      const response = await fetch('/api/promotion/sms/sign/list');
      const result = await response.json();

      if(result.success && result?.data?.code === 'OK') {
        const data = result.data?.smsSignList || [];
        setSigns(data);
      }
    }catch (error) {
      console.error('获取签名失败:', error);
      Modal.error({
        title: '错误',
        content: '获取短信签名失败，请稍后重试',
      });
    }
  })

  // 模拟获取客户数据
  const fetchCustomers = useCallback(async (filters = {}) => {
    setLoadingCustomers(true);
    const { page = 1, pageSize = 10, ...others } = filters;

    try {
      // 实际应用中替换为API调用
      const response = await fetch(`/api/customer/query?page=${page}&pageSize=${pageSize}`, { 
        method: 'POST', 
        headers: {
          'Content-Type': 'application/json',
        }, 
        body: JSON.stringify(others) }
      );

      const result = await response.json();
      
      // 设置前5个客户用于预览
      setSelectedCustomers(result?.data?.rows || []);
      setSelectedTotal(result?.data?.count || 0);
      setTotal(result?.data?.count || 0);
      setCurrent(page);
      
    } catch (error) {
      console.error('获取客户数据失败:', error);
      Modal.error({
        title: '错误',
        content: '获取客户数据失败，请稍后重试',
      });
    } finally {
      setLoadingCustomers(false);
    }
  }, []);


  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const shopId = await getCurrentShopId();
      // 构建任务数据
      const taskData = {
        shopId,
        name: values.name,
        type: taskType,
        status: 2,
        sendTime: values.sendTime,
        days: values.days,

        smsContent: {
          templateCode: selectedTemplate.templateCode,
          templateContent: selectedTemplate.templateContent,
          signName: selectedSign.signName,
        },
        // 客户筛选参数
        customerCondition: {
          remark: values.remark || null,
          accId: window.__baseInfo.accId,
          shopId: shopId,
          phoneNo: values.phoneNo || null,
          totalConsumeMin: values.totalConsumeMin || null,
          totalConsumeMax: values.totalConsumeMax || null,
          latestOrderDays: values.latestOrderDays || null,
          createTimeStart: values.createTimeStart || null,
          createTimeEnd: values.createTimeEnd || null,
        }
      };
      
      console.log('提交任务数据:', taskData);
      
      // 实际应用中替换为API调用
      const response = await fetch('/api/promotion/sms/task/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskData),
      });

      const result = await response.json();

      if(result.success) {
        message.success('任务创建成功！');
        setTimeout(() => {
          window.location.href = '/promotion/sms';
        }, 300);
      } else {
        message.error(result.data);
      }
    } catch (error) {
      console.error('创建任务失败:', error);
    }
  };

  // 处理模板选择
  const handleTemplateSelect = (templateCode) => {
    const template = templates.find(t => t.templateCode === templateCode);

    setSelectedTemplate(template || {});
  };

  const handleSignSelect = (signName) => {
    const sign = signs.find(s => s.signName === signName);
    setSelectedSign(sign || {});
  };

  // 处理客户筛选
  const handleFilterChange = async (page = 1, pageSize = 10) => {
    const values = form.getFieldsValue();
    const shopId = await getCurrentShopId();

    fetchCustomers({
      shopId,
      totalConsumeMin: values.totalConsumeMin,
      totalConsumeMax: values.totalConsumeMax,
      createTimeStart: values.createTimeStart,
      createTimeEnd: values.createTimeEnd,
      phoneNo: values.phoneNo,
      remark: values.remark,
      latestOrderDays: values.latestOrderDays,
      page,
      pageSize,
    });
  };

  // 初始化数据
  useEffect(() => {
    fetchTemplates();
    fetchSigns();
    // fetchCustomers();
    const taskId = url.searchParams.get('taskId');

    if (taskId) {
      initFome(taskId);
    }
  }, []);

  // 任务类型变更
  const handleTaskTypeChange = (e) => {
    setTaskType(e.target.value);

    if (e.target.value === 'CYC') {
      setPeriodicConfigVisible(true);
    } else {
      setPeriodicConfigVisible(false);
      form.setFieldsValue({ cronExpression: undefined });
    }
  };

  const cancelTaskComfirm = () => {
    const taskId = url.searchParams.get('taskId');

    Modal.confirm({
      title: '取消任务',
      content: '确定要取消此任务吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        cancelTask(taskId);
      }
    })
  }

  const deleteTaskComfirm = () => {
    const taskId = url.searchParams.get('taskId');

    Modal.confirm({
      title: '删除任务',
      content: '确定要删除此任务吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        deleteTask(taskId);
      }
    })
  }

  const cancelTask = async (taskId) => {
    try {
      const shopId = await getCurrentShopId();
      const response = await fetch(`/api/promotion/sms/task/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ taskId, shopId }),
      });
      const result = await response.json();

      if(result?.success) {
        message.success('取消任务成功！');

        setTimeout(() => {
          window.location.href = '/promotion/sms';
        }, 300);
      }else {
        message.error(result.message);
      }

    }catch (error) {
      message.error('取消任务失败！');
    }
  }

  const deleteTask = async (taskId) => { 
    try {
      const shopId = await getCurrentShopId();
      const response = await fetch(`/api/promotion/sms/task/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ taskId, shopId }),
      });
      const result = await response.json();

      if(result?.success) {
        message.success('删除任务成功！');

        setTimeout(() => {
          window.location.href = '/promotion/sms';
        }, 300);
      }else {
        message.error(result.message);
      }

    }catch (error) {
      message.error('删除任务失败！');
    }
  }

  return (
    <div className="marketing-task-form">
      <SwitchShop />
      <h2>创建短信营销任务</h2>
      <Form
          form={form}
          layout="vertical"
          disabled={[4, 6].includes(taskStatus)}
          // onFinish={handleSubmit}
          // onValuesChange={handleFilterChange}
        >
          {/* 任务基本信息 */}
          <Card 
            title={<span>任务基本信息</span>} 
            style={{ marginBottom: 16 }}
            headStyle={{ backgroundColor: '#fafafa' }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="任务名称"
                  rules={[{ required: true, message: '请输入任务名称' }]}
                >
                  <Input 
                    placeholder="例如：618大促提醒" 
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="任务类型"
                  name="type"
                  rules={[{ required: true, message: '请选择任务类型' }]}
                  initialValue="ONCE"
                >
                  <Radio.Group 
                    value={taskType} 
                    onChange={handleTaskTypeChange}
                    style={{ width: '100%' }}
                    options={[
                      { label: '一次性任务', value: 'ONCE' },
                      { label: '周期性任务(365天内)', value: 'CYC' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}> 
                <Form.Item
                  name="sendTime"
                  label="开始发送时间"
                  rules={[{ required: true, message: '请选择发送时间' }]}
                >
                  <DatePicker
                    showTime
                    format="YYYY-MM-DD HH:mm"
                    placeholder="选择时间"
                    disabledDate={current => current && current < dayjs().startOf('day')}
                    disabledTime={() => ({disabledHours: () => [0, 1, 2, 3, 4, 5, 22, 23, 24]})}
                    style={{ width: '100%' }}
                    changeOnScroll={true}
                    needConfirm={false}
                    showNow={true}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                {periodicConfigVisible && (
                  <Form.Item
                    label="周期配置"
                    name={'days'}
                    rules={[
                      {
                        required: true,
                        message: '请输入周期配置',
                      }
                    ]}
                    help="请注意控制发送频率，以防用户加入黑名单"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      prefix="每"
                      suffix="天发送一次"
                      min={1}
                      max={365}
                      step={1}
                      onChange={(value) => {
                        // setPeriodicConfig(value);
                      }}
                      placeholder="请输入天数"
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>
          </Card>

          {/* 短信内容设置 */}
          <Card 
            title={<span>短信内容设置</span>} 
            style={{ marginBottom: 16 }}
            headStyle={{ backgroundColor: '#fafafa' }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="templateCode"
                  label="短信模板"
                  rules={[{ required: true, message: '请选择短信模板' }]}
                >
                  <Select 
                    placeholder="选择短信模板" 
                    loading={loadingTemplates}
                    onChange={handleTemplateSelect}
                    style={{ width: '100%' }}
                  >
                    {templates.map(template => (
                      <Option key={template.templateCode} value={template.templateCode}>
                        {template.templateName + '：' + template.templateContent}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item 
                  label="短信签名"
                  name="signName"
                  rules={[{ required: true, message: '请选择短信签名' }]}
                >
                  <Select
                    placeholder="选择短信签名" 
                    onChange={handleSignSelect}
                    options={signs.map(sign => ({ value: sign.signName, label: sign.signName }))} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item 
                  label="短信预览"
                > 
                  <div type="secondary" style={{border: '1px solid #d9d9d9', padding: 8, width: '100%'}}>
                    {selectedSign.signName ? `【${selectedSign.signName}】` : ''}{selectedTemplate.templateContent || '请选择短信模板和签名'}
                  </div>
                </Form.Item>
              </Col>
              <Col> 
                <Button 
                  type="primary"
                  onClick={handleTestSend}
                >
                  发送测试
                </Button>
              </Col>
            </Row>
          </Card>
          
          {/* 客户筛选条件 */}
          <Card 
            title={
              <Space>
                <span>客户筛选条件</span>
              </Space>
            } 
            style={{ marginBottom: 16 }}
            headStyle={{ backgroundColor: '#fafafa' }}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="客户电话" name={'phoneNo'}>
                  <Input placeholder="请输入客户电话" /> 
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="消费金额范围 (元)"
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item
                        name="totalConsumeMin"
                        noStyle
                      >
                        <Input 
                          placeholder="最小值" 
                          type="number" 
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="totalConsumeMax"
                        noStyle
                      >
                        <Input 
                          placeholder="最大值" 
                          type="number" 
                          min={0}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="注册时间范围"
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item
                        name="createTimeStart"
                        noStyle
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="YYYY-MM-DD"
                          placeholder="开始时间"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="createTimeEnd"
                        noStyle
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="YYYY-MM-DD"
                          placeholder="结束时间"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item
                      name="latestOrderDays"
                      label="最近未消费天数"
                    >
                      <Select placeholder="选择未消费天数" allowClear>
                        <Option value={7}>7天未消费</Option>
                        <Option value={30}>30天未消费</Option>
                        <Option value={60}>60天未消费</Option>
                        <Option value={90}>90天未消费</Option>
                        <Option value={180}>半年未消费</Option>
                        <Option value={365}>一年未消费</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="remark"
                      label="备注"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                
              </Col>
            </Row>
            <div>
              <Button 
                type="primary"
                onClick={() => handleFilterChange(1)}
              >
                筛选
              </Button>
            </div>
            
            <Form.Item
              label="筛选客户预览"
            >
              <>
                
                {loadingCustomers ? (
                  <Skeleton active paragraph={{ rows: 3 }} />
                ) : (
                  <Table
                    rowKey="customerId"
                    columns={columns}
                    dataSource={selectedCustomers}
                    pagination={{...pagination, simple: true, showSizeChanger: false}}
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                )}
              </>
            </Form.Item>
            
            <Alert
              message={`预计发送给 ${selectedTotal} 位客户，此数量可能会随您客户数量的增减实时变化`}
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          </Card>
          
          <Space>
            {taskStatus === 0 && <Button 
              onClick={handleSubmit}
              type="primary"
            >
              提交
            </Button>}

            {(taskStatus === 2 || taskStatus === 3) && <Button 
              onClick={cancelTaskComfirm}
              type="primary"
            >
              取消任务
            </Button>}

            {taskStatus === 6 && <Button 
              onClick={deleteTaskComfirm}
              type="primary"
              disabled={false}
            >
              删除任务
            </Button>}

            <Button href='/promotion/sms' disabled={false}>
              返回
            </Button>
          </Space>
        </Form>
    </div>
  );
};

export default MarketingTaskForm;